import type { DividerPolicy } from "@CommonTypes/backend/PspRules";
import { getBaseDividerEvidence } from "@CommonUtils/pog/dividerPolicy";
import {
  applyTellOProposal,
  proposeTellOClear,
  proposeTellOKeep,
  proposeTellOLikeBase,
  proposeTellOLoaderMode,
  proposeTellOProducts,
} from "./build";
import { buildTellODictionary, largestTellOGroup } from "./dictionary";
import { localizedTellOGroupLabel } from "./copy";
import { tellOEnglish, type TellOTranslate } from "./messages";
import { tellOFacts, tellOLoaderEvidence, tellOTicks, type TellOContext } from "./facts";
import { tellOTypicalWidth } from "./grammar";
import { displayTellOLength } from "./lengths";
import { TELL_O_MAX_CHIPS, type TellOActionProposal, type TellORuleProposal } from "./proposal";

export type TellOChip =
  | { kind: "apply"; label: string; proposal: TellOActionProposal }
  | {
      kind: "prefill";
      label: string;
      text: string;
      selection: [number, number];
      canonical?: { before: string; after: string };
    };

const prefill = (
  key: "prefillWidth" | "prefillDepth",
  number: string,
  unit: string,
  t: TellOTranslate,
): TellOChip => {
  const marker = "{{number}}";
  const template = t(key, { number: marker, unit });
  const start = template.indexOf(marker);
  const before = template.slice(0, start);
  const after = template.slice(start + marker.length);
  const canonicalTemplate = tellOEnglish(key, { number: marker, unit });
  const canonicalStart = canonicalTemplate.indexOf(marker);
  const text = `${before}${number}${after}`;
  return {
    kind: "prefill",
    label: text,
    text,
    selection: [before.length, before.length + number.length],
    ...(template === canonicalTemplate
      ? {}
      : {
          canonical: {
            before: canonicalTemplate.slice(0, canonicalStart),
            after: canonicalTemplate.slice(canonicalStart + marker.length),
          },
        }),
  };
};

// The grammar consumes English commands; the displayed template follows the locale.
export const canonicalTellOChipText = (chip: TellOChip | null, text: string): string => {
  if (!chip || chip.kind !== "prefill" || !chip.canonical) return text;
  const before = chip.text.slice(0, chip.selection[0]);
  const after = chip.text.slice(chip.selection[1]);
  if (!text.startsWith(before) || !text.endsWith(after)) return text;
  const number = text.slice(before.length, text.length - after.length);
  return `${chip.canonical.before}${number.replace(",", ".")}${chip.canonical.after}`;
};

const sameSet = (a: ReadonlySet<string>, b: ReadonlySet<string>) =>
  a.size === b.size && [...a].every((code) => b.has(code));

const isNoOp = (context: TellOContext, proposal: TellORuleProposal) => {
  const after: DividerPolicy = applyTellOProposal(context.policy, proposal);
  const change = proposal.change;
  if (change.type === "keep") return (context.policy.keepFixtureDividers === true) === change.keep;
  if (change.type === "loader-mode") {
    return (context.policy.loaderMode ?? "products") === change.mode;
  }
  if (change.type === "like-base") return context.policy.source === "base";
  const before = tellOTicks(context, context.policy);
  const next = tellOTicks(context, after);
  return sameSet(before.dividers, next.dividers) && sameSet(before.loaders, next.loaders);
};

const isClearedList = (policy: DividerPolicy) =>
  policy.source === "items" && (policy.productCodes ?? []).length === 0;

const baseState = (context: TellOContext) => {
  if (!context.basePog) return "unreadable" as const;
  const evidence = getBaseDividerEvidence(context.basePog);
  if (evidence.hardware === null && evidence.widthNotBelowShelf + evidence.flagsOutOfRange > 0) {
    return "unreadable" as const;
  }
  if (evidence.dividerShelves === 0 && tellOLoaderEvidence(context).loaderShelves === 0) {
    return "empty" as const;
  }
  return "readable" as const;
};

export const buildTellOChips = (
  context: TellOContext,
  { afterApply = false }: { afterApply?: boolean } = {},
  t: TellOTranslate = tellOEnglish,
): TellOChip[] => {
  const unit = context.unit;
  const dictionary = buildTellODictionary({
    products: context.products,
    levelNames: context.levelNames,
  });
  const group = largestTellOGroup(dictionary);
  const width = displayTellOLength(tellOTypicalWidth(context));
  const onlyGroup = group
    ? proposeTellOProducts(context, { features: ["dividers"], op: "only", group })
    : null;
  const applyChip = (label: string, proposal: TellOActionProposal): TellOChip => ({
    kind: "apply",
    label,
    proposal,
  });
  const rule = (label: string, proposal: TellORuleProposal | null): TellOChip[] =>
    proposal && !isNoOp(context, proposal) ? [applyChip(label, proposal)] : [];

  const chips: TellOChip[] = afterApply ? [applyChip(t("undo"), { kind: "undo" })] : [];
  const state = baseState(context);

  if (state !== "readable") {
    const evidence = tellOLoaderEvidence(context);
    const depth = displayTellOLength(evidence.typicalDepth ?? (unit === "cm" ? 400 : 150));
    chips.push(
      ...rule(
        t("dividersAll"),
        proposeTellOProducts(context, { features: ["dividers"], op: "all" }),
      ),
      ...(group
        ? rule(t("dividersOnly", { group: localizedTellOGroupLabel(group, t) }), onlyGroup)
        : []),
      prefill("prefillWidth", String(width), unit, t),
      prefill("prefillDepth", String(depth), unit, t),
      // Shown even when nothing is ticked: it stores the "none" an unread base only implies.
      ...(state === "unreadable" && !isClearedList(context.policy)
        ? [applyChip(t("noDividers"), proposeTellOClear(context, "dividers"))]
        : []),
    );
    return chips.slice(0, TELL_O_MAX_CHIPS);
  }

  const facts = tellOFacts(context, context.policy);
  const dividersCleared = context.policy.source !== "base" && facts.dividerProducts === 0;
  if (dividersCleared) {
    chips.push(
      ...rule(
        t("dividersAll"),
        proposeTellOProducts(context, { features: ["dividers"], op: "all" }),
      ),
      ...rule(t("likeBase"), proposeTellOLikeBase(context)),
    );
  } else {
    chips.push(...rule(t("noDividers"), proposeTellOClear(context, "dividers")));
  }
  chips.push(...rule(t("noLoaders"), proposeTellOClear(context, "loaders")));
  chips.push(
    context.policy.keepFixtureDividers
      ? applyChip(t("chipDoNotKeep"), proposeTellOKeep(false))
      : applyChip(t("chipKeep"), proposeTellOKeep(true)),
  );
  chips.push(
    context.policy.loaderMode === "shelf"
      ? applyChip(t("chipLoaderProducts"), proposeTellOLoaderMode("products"))
      : applyChip(t("chipLoaderShelf"), proposeTellOLoaderMode("shelf")),
  );
  if (group)
    chips.push(
      ...rule(t("dividersOnly", { group: localizedTellOGroupLabel(group, t) }), onlyGroup),
    );
  chips.push(prefill("prefillWidth", String(width), unit, t));
  return chips.slice(0, TELL_O_MAX_CHIPS);
};
